/**
 * Simplified Coaching Handler for PracticeTest
 *
 * Used for ALL practice tests (theory, road signs, hazard perception, etc.).
 * Single source of truth: questionCoaching.ts + coachingHandler.ts.
 *
 * Flow (simplified):
 * 1. User answers wrong → initCoaching() → Round 1 reflective cards (e.g. "I guessed", "I misread it", "It felt right")
 * 2. User clicks card → take-home rule → move-on (no coach response after card, no Round 2)
 *
 * HARD GLOBAL RULE – Practice questions chat:
 * After a user round 1 follow-up (card click or voice), the agent must respond with
 * exactly 2 messages, no more: (1) explanation, (2) take-home/move-on only.
 * No third bubble, no duplicate rule (long + short). Enforced in createSimpleHandleFollowUp.
 *
 * RULE: No follow-ups must appear after the move-on message. We clear suggestions
 * when coaching completes and again before adding the move-on message; UI hides
 * chips when isCoachingComplete(questionId).
 */

import { Dispatch } from 'react';
import { PracticeTestAction, PracticeTestState } from './PracticeTest.reducer';
import type { Question } from '../../types';
import type { ChatMessageData } from '../../types/ChatMessageData';
import {
  initCoaching,
  getCurrentCards,
  handleCardClick,
  CoachingState
} from '../../services/coachingHandler';
import { getFallbackResponse } from '../../services/theoryCoach.core';
import type { TheoryCoachContext } from '../../services/theoryCoach.types';
import { getCurrentSessionToken, isTokenCurrent } from '../../utils/asyncGuard';
import { safeArrayAt } from '../../utils/safeRender';

// Store coaching state per question
const coachingStates = new Map<string, CoachingState>();
const getMicroPauseMs = () => 0;

/** True when both strings are the same rule (one contains the other or they share key wording). Avoids duplicate long/short rule bubbles. */
function _sameRuleShort(a: string, b: string): boolean {
  const x = a.trim().toLowerCase();
  const y = b.trim().toLowerCase();
  if (!x || !y) return false;
  if (x === y) return true;
  if (x.includes(y) || y.includes(x)) return true;
  const minLen = Math.min(x.length, y.length);
  if (minLen >= 30 && x.slice(0, 40) === y.slice(0, 40)) return true;
  return false;
}

/** Signature: (question, language, dispatch, questionIndex?) */
export type InitializeCoachingFn = (
  question: Question,
  language: 'en' | 'nl' | 'ar',
  dispatch: Dispatch<PracticeTestAction>,
  questionIndex?: number
) => void;

/**
 * Initialize coaching when user answers incorrectly
 * CRITICAL: Only initialize ONCE per question - prevents re-initialization on state changes
 * questionIndex ensures different follow-ups per position (avoids same prompts when question IDs repeat).
 */
export const initializeCoaching: InitializeCoachingFn = (
  question,
  language,
  dispatch,
  questionIndex
): void => {
  // PREVENT RE-INITIALIZATION: If coaching already exists for this question, don't reinitialize
  // This prevents cards from reappearing after they've been cleared
  const existingState = coachingStates.get(question.id);
  if (existingState) {
    // Check if coaching is complete - if so, don't show cards again
    if (existingState.isComplete) {
      // Coaching already complete - ensure cards stay hidden
      dispatch({ type: 'SET_FOLLOW_UP_SUGGESTIONS', payload: [] });
    }
    // Coaching already started for this question - don't reset
    return;
  }
  
  // RESET: Clear any existing coaching state for OTHER questions
  // This prevents stale state from previous questions
  coachingStates.clear();
  
  const correctOption = question.options?.find(opt => opt.id === question.correctAnswerId);
  
  const state = initCoaching(
    question.id,
    question.text,
    question.subject || 'General',
    correctOption?.text || '',
    question.explanation || '',
    language,
    questionIndex
  );
  
  coachingStates.set(question.id, state);
  
  // RESET: Clear chat state for new question
  dispatch({ type: 'SET_FOLLOW_UP_COUNT', payload: 0 });
  
  // Set Round 1 cards for this specific question
  const cards = getCurrentCards(state);
  dispatch({ type: 'SET_FOLLOW_UP_SUGGESTIONS', payload: cards });
}

/**
 * Handle card click - main entry point
 * @param message - The user's message or card text
 * @param _isVoice - Optional, for backwards compatibility (not used)
 */
export function createSimpleHandleFollowUp(
  state: PracticeTestState,
  questions: Question[],
  currentLanguage: string,
  dispatch: Dispatch<PracticeTestAction>
): (message: string, isVoice?: boolean) => Promise<void> {
  return async (message: string, _isVoice?: boolean) => {
    const currentQ = safeArrayAt(questions, state.currentQuestionIndex);
    if (!currentQ) return;
    
    const lang = (currentLanguage || 'nl') as 'en' | 'nl' | 'ar';
    const questionId = currentQ.id;
    
    // Get or create coaching state
    let coachingState = coachingStates.get(questionId);
    if (!coachingState) {
      initializeCoaching(currentQ, lang, dispatch, state.currentQuestionIndex);
      coachingState = coachingStates.get(questionId)!;
    }
    
    // Add user message to chat
    const userMessage: ChatMessageData = {
      id: `coaching-user-${state.currentQuestionIndex}-${Date.now()}`,
      role: 'user',
      type: 'chat',
      questionId,
      content: message,
      timestamp: new Date()
    };
    dispatch({ type: 'ADD_CHAT_MESSAGE', payload: userMessage });

    // Round 1 follow-up (voice or tap): get contextual coach reply for user message (e.g. "I misread it" → "Take your time, read carefully.")
    const correctOption = currentQ.options?.find(opt => opt.id === currentQ.correctAnswerId);
    const coachContext: TheoryCoachContext = {
      userMessage: message,
      language: lang,
      currentQuestion: {
        text: currentQ.text,
        options: (currentQ.options ?? []).map((opt, idx) => ({
          letter: String.fromCharCode(65 + idx),
          text: opt.text
        })),
        correctAnswer: correctOption?.text ?? '',
        explanation: currentQ.explanation,
        topic: currentQ.subject ?? 'General'
      }
    };
    const coachReply = getFallbackResponse(coachContext);

    // Handle the card click (returns take-home + move-on; response is empty in current flow)
    const result = handleCardClick(coachingState, message);

    coachingState.currentRound = result.newRound;

    const takeHomeMessage = result.takeHome?.trim() || '';
    // Last message is always plain move-on only (no "Well done!" etc.)
    const moveOnMessage = lang === 'nl' ? 'Tik op Doorgaan als je klaar bent.' : lang === 'ar' ? 'اضغط على تابع عندما تكون جاهزاً.' : "Tap Continue when you're ready.";
    const responseToShow = (result.response && result.response.trim()) ? result.response : coachReply;

    // If not complete, show next cards and increment count
    if (!result.isComplete && result.nextCards.length > 0) {
      dispatch({ type: 'SET_FOLLOW_UP_COUNT', payload: state.followUpCount + 1 });
      dispatch({ type: 'SET_FOLLOW_UP_SUGGESTIONS', payload: result.nextCards });
      return;
    }

    // COMPLETE: Hard global rule – exactly 2 coach messages; async token for Rule 3
    const asyncToken = getCurrentSessionToken();
    dispatch({ type: 'SET_FOLLOW_UP_SUGGESTIONS', payload: [] });
    coachingState.isComplete = true;

    // Message 1: explanation only (e.g. "You must give way to trams at junctions.") – no move-on text
    const moveOnPhrase = /tap continue|tik op doorgaan|اضغط على تابع/i;
    const message1Explanation = takeHomeMessage || (responseToShow.trim() && !moveOnPhrase.test(responseToShow) ? responseToShow : '');
    // Message 2: last message is always plain "Say continue or tap when you're ready." only
    const message2TakeHomeMoveOn = moveOnMessage;

    // Message 1: Explanation - NO AUTO VOICE for production safety
    if (message1Explanation.trim()) {
      const coachMessage1: ChatMessageData = {
        id: `coaching-coach-1-${state.currentQuestionIndex}-${Date.now()}`,
        role: 'coach',
        type: 'chat',
        questionId,
        content: message1Explanation.trim(),
        timestamp: new Date()
      };
      dispatch({ type: 'ADD_CHAT_MESSAGE', payload: coachMessage1 });
      // REMOVED: Automatic voice playback disabled for production safety
      // Voice is now ONLY triggered by explicit user actions
    }

    // 2. Take-home/move-on only – abort if stale (Rule 3) - NO AUTO VOICE
    if (message2TakeHomeMoveOn.trim() && isTokenCurrent(asyncToken)) {
      await new Promise(resolve => setTimeout(resolve, getMicroPauseMs()));
      await addDelayedMessage(dispatch, state, questionId, message2TakeHomeMoveOn.trim(), lang, getMicroPauseMs(), asyncToken);
    }

    dispatch({ type: 'SET_FOLLOW_UP_COUNT', payload: 2 });
  };
}

/**
 * Add a delayed message to chat. Aborts if asyncToken is no longer current (Rule 3).
 * NO AUTO VOICE - voice is now only triggered by explicit user actions
 */
async function addDelayedMessage(
  dispatch: Dispatch<PracticeTestAction>,
  state: PracticeTestState,
  questionId: string,
  content: string,
  lang: 'en' | 'nl' | 'ar',
  delayMs: number,
  asyncToken?: number
): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, delayMs));
  if (asyncToken !== undefined && !isTokenCurrent(asyncToken)) return;
  
  const message: ChatMessageData = {
    id: `coaching-coach-${Date.now()}`,
    role: 'coach',
    type: 'chat',
    questionId,
    content,
    timestamp: new Date()
  };
  dispatch({ type: 'ADD_CHAT_MESSAGE', payload: message });
  
  // REMOVED: Automatic voice playback disabled for production safety
  // Voice is now ONLY triggered by explicit user actions (button taps)
}

/**
 * Clean up coaching state for a question
 */
export function cleanupCoaching(questionId: string): void {
  coachingStates.delete(questionId);
}

/**
 * Reset all coaching states
 */
export function resetAllCoaching(): void {
  coachingStates.clear();
}

/**
 * Check if coaching is complete for a question
 */
export function isCoachingComplete(questionId: string): boolean {
  const state = coachingStates.get(questionId);
  return state?.isComplete ?? false;
}

/**
 * Get current round for a question
 */
export function getCurrentRound(questionId: string): 1 | 2 {
  const state = coachingStates.get(questionId);
  return state?.currentRound ?? 1;
}
